/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2018, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../utils/DatasourceUtil',
	'./SynchContextHelper',
	'./SynchronizeDataHelper'
], function(
	_,
	DatasourceUtil,
	SynchContextHelper,
	SynchronizeDataHelper
) {

	/**
	* Class handling joined table column data within same data source
	*/
	class SynchJoinColumn {
		constructor(column, module, handlers) {
			this.module = module;
			this.column = column;
			this.handlers = handlers;
			this.joinedTableValues = {};
		}

		getItemId () {
			return this.column.getId();
		}

		getLabel () {
			return this.column.getLabel();
		}

		getTableRef () {
			this.column.getTableName();
		}

		matchedColumnByName (itemIdToMatch) {
			var columnToVerify = this.module.getMetadataColumn(itemIdToMatch);
			return columnToVerify ? (this.column.getLabel().toLowerCase() === columnToVerify.getLabel().toLowerCase()) : false;
		}

		getJointTupleValue(itemIdToMatch, valueToMatch) {
			let matchedItemId = this.joinedTableValues[itemIdToMatch];
			if (matchedItemId) {
				return matchedItemId[valueToMatch.u];
			}
		}

		matchJointedTableValue (column, valueToMatch) {
			let sourceColumn = this.module.getMetadataColumn(column.getItemId());
			//Column does not exist in this module so return null
			if (!sourceColumn) {
				return Promise.resolve(null);
			}

			let sourceTableRef = sourceColumn.getTableName();
			let currentTableRef = this.column.getTableName();

			//Does not point to the same table and these tables are joined
			if ((currentTableRef !== sourceTableRef) && DatasourceUtil.haveTableJoinsInSameDataSource(this.module, [currentTableRef], [sourceTableRef])) {
				let result;
				let sourceColumnIsOlap = sourceColumn.isOlapColumn();
				let numeric = !this.column.isOlapColumn() && this.column.isNumericDataType();
				let joinedTableEntry = this.getJointedTableEntry(sourceColumn.getId());
				let dateTime = !this.column.isOlapColumn() && this.column.isDateTimeDataType();

				if (!sourceColumnIsOlap && (numeric || dateTime)) {
					if (numeric) {
						result = joinedTableEntry[valueToMatch.u] = valueToMatch;
					} else {
						let clonedValue = SynchronizeDataHelper.clone(valueToMatch);
						if (SynchContextHelper.replaceUseValue(this.column, clonedValue)) {
							result = joinedTableEntry[valueToMatch.u] = clonedValue;
						}
					}
					return Promise.resolve(result);
				} else {
					return this.handlers.loadValues(this.module, this.column.getId())
						.then(values => {
							values = values || [];
							result = _.find(values, value => {
								return valueToMatch.d === value.d;
							});

							if (result) {
								joinedTableEntry[valueToMatch.u] = result;
							}
							return result;
						});
				}
			}
			return Promise.resolve(null);
		}

		getJointedTableEntry (itemId) {
			this.joinedTableValues[itemId] = this.joinedTableValues[itemId] || {};
			return this.joinedTableValues[itemId];
		}
	}

	/**
	* Class responsible for synchronize joined table data within same data source
	*/
	class SynchJoinTable {
		constructor (module, options = {}) {
			if (!module) {
				throw new Error('module object is not provided.');
			}
			if (!options.handlers) {
				throw new Error('handlers object is not provided.');
			}

			this.module = module;
			this.handlers = options.handlers;
			this.columns = {};
		}

		getColumn (itemId) {
			if (this.columns[itemId]) {
				return this.columns[itemId];
			}

			let metadataColumn = this.module.getMetadataColumn(itemId);
			if (metadataColumn) {
				this.columns[itemId] = new SynchJoinColumn(metadataColumn, this.module, this.handlers);
			}

			return this.columns[itemId];
		}

		getColumns () {
			return this.columns;
		}
	}

	return SynchJoinTable;

});
